import type { NextPage } from 'next'

import type { BannerCTAProps } from '~/components/atoms/BannerCTA/BannerCTA'
import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import type { DocumentDownloadListProps } from '~/components/atoms/DocumentDownloadList/DocumentDownloadList'
import type { FullWidthImageProps } from '~/components/atoms/FullWidthImage/FullWidthImage'
import type { PressContactDetailsProps } from '~/components/atoms/PressContactDetails/PressContactDetails'
import type { FeaturedArticlesProps } from '~/components/blocks/FeaturedArticles/FeaturedArticles'
import FeaturedArticles from '~/components/blocks/FeaturedArticles/FeaturedArticles'
import PressFilter from '~/components/blocks/PressFilter/PressFilter'
import { ComponentTheme } from '~/components/themes'
import {
  featuredArticles,
  getBody,
  image,
  internationalizedField,
  link,
  myPortfolioBanner,
  pageLanguageFilter,
  pressReleases,
} from '~/schemas/common'
import { useSanity } from '~/services/sanity'
import { buildBreadcrumb } from '~/utils/breadcrumb'
import { buildTranslatedUrlsQuery } from '~/utils/getHreflangUrls'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import { createLangStaticPaths, supportedLocales } from '~/utils/langPaths'
import { urlQuery } from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

export interface PageData {
  title: string
  pressReleases: DocumentDownloadListProps
  featuredArticles: FeaturedArticlesProps
  pressContactDetails: PressContactDetailsProps
  fullWidthImage: FullWidthImageProps
  body: any
  bannerCTA: BannerCTAProps
  breadcrumb: BreadcrumbProps['list']
  pressCategories: { name: string; slug: string }[]
}

const Press: NextPage<PageData> = (props: PageData) => {
  const { featuredArticles, breadcrumb, pressCategories } = props

  return (
    <>
      <Breadcrumb list={breadcrumb} theme={ComponentTheme.primary} />
      {pressCategories.length > 0 && (
        <PressFilter categories={pressCategories} />
      )}
      {featuredArticles && (
        <FeaturedArticles
          {...featuredArticles}
          theme={ComponentTheme.primary}
          title={null}
        />
      )}
    </>
  )
}

export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    try {
      const { client, limiter, scorer } = await useSanity(context)
      const lang = (context.params?.lang as Locale) || 'en'
      const messages = await loadMessages(lang)

      const data = await client.fetch(
        `*[_type == 'page' && slug.current == $slug && ${pageLanguageFilter(limiter)} ${limiter}] ${scorer}[0]{
        ${urlQuery},
        ${buildTranslatedUrlsQuery([...supportedLocales])},
        title,
        "fullWidthImage": body[_type == 'fullWidthImage'][0]{${image}},
        "bannerCTA": body[_type == 'bannerCTA'][0]{
          ...,
          ${link},
        },
        ${getBody(lang)},
        ${pressReleases},
        ${featuredArticles},
        hasGoogleReviews,
        fullGoogleReviews,
        "pressCategories": *[_type == 'category' && type == 'press'] {
          "name": ${internationalizedField('name')},
          "slug": slug.current,
        },
        ${myPortfolioBanner}
        "pressContactDetails": body[_type == 'collectionSelector' && value == 'pressContactDetails'][0]
    }`,
        {
          slug: 'press',
          lang: lang,
        }
      )

      const breadcrumbPieces = ['press-and-media', 'all-articles']
      const breadcrumb: BreadcrumbProps['list'] | undefined = buildBreadcrumb(
        breadcrumbPieces,
        '',
        undefined,
        messages,
        lang
      )

      const originalProps = {
        ...data,
        breadcrumb: breadcrumb,
        meta: {
          title: `Press and Media | MyArtBroker`,
        },
      }

      return {
        props: await addTranslations(originalProps, context),
      }
    } catch (e) {
      console.log('error:', e)
      return { props: {} }
    }
  }
)
export const getStaticPaths = async () => createLangStaticPaths()

export default Press
